import React, { useEffect, useState, useRef } from 'react'

import { PROMO_CODE_PATTERN } from '../addPromoCodeValidation'

import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import InputGroup from 'react-bootstrap/InputGroup'
import Stack from 'react-bootstrap/Stack'

import { WarningCard } from '../../../WarningCard'

export default function Details({
  initialEditState,
  isEditing,
  isLoading,
  priceOpts,
  initialPriceOption,
  priceOption,
  setPriceOption,
  applyToOpts,
  appliedTo,
  setAppliedTo,
  limitOpts,
  limitOption,
  setLimitOption,
  limit,
  setLimit,
  code,
  setCode,
  discount,
  setDiscount,
  handleValid,
  findError,
  errors,
  discountCapMessage,
}) {
  const limitInputRef = useRef(null)
  const prevPriceOptionRef = useRef(null)

  const [codeError, setCodeError] = useState()

  const [discountError, setDiscountError] = useState()

  useEffect(() => {
    if (isEditing) {
      if (initialEditState) {
        prevPriceOptionRef.current = initialEditState?.priceOption
      }
    } else {
      prevPriceOptionRef.current = priceOpts[0].value
    }
  }, [isEditing, initialEditState])

  // find the code error
  useEffect(() => {
    setCodeError(findError('code'))
  }, [errors])

  // find the promo code discount error
  useEffect(() => {
    setDiscountError(findError('discount'))
  }, [errors])

  // focus limit input field whenever limit option changes
  useEffect(() => {
    if (!limit) {
      if (limitInputRef.current) {
        limitInputRef.current.focus()
      }
    }
    // limit has value
    // reset limit when limit option is not set to fixed amount
    else {
      if (limitOption !== limitOpts[1].value) {
        setLimit()
      }
    }
  }, [limit, limitOption])

  // focus limit input field whenever limit option changes
  useEffect(() => {
    if (!limit) {
      if (limitInputRef.current) {
        limitInputRef.current.focus()
      }
    }
    // limit has value
    // reset limit when limit option is not set to fixed amount
    else {
      if (limitOption !== limitOpts[1].value) {
        setLimit()
      }
    }
  }, [limit, limitOption])

  // remove discount value whenever price option changes
  useEffect(() => {
    const prevPriceOption = prevPriceOptionRef.current
    // Only reset discount if:
    // - the price option has actually changed (not on mount)
    // - there is a discount
    if (prevPriceOption !== priceOption) {
      if (discount) {
        setDiscount('')
      }
    }
    // Update ref for next render
    prevPriceOptionRef.current = priceOption
  }, [discount, priceOption])

  const isPercentDiscount = priceOption === priceOpts[1]?.value

  return (
    <Modal.Body className="pb-0">
      <Stack gap={3}>
        <Form.Group className="form-group" controlId="code">
          <Form.Label>Enter promo code</Form.Label>
          <Form.Control
            type="text"
            maxLength="15"
            name="code"
            pattern={PROMO_CODE_PATTERN.source}
            placeholder="Enter promo code"
            value={code || ''}
            onChange={(e) => setCode(e.target.value)}
            onBlur={handleValid}
            className={`${codeError ? 'error-border' : ''}`}
          />
        </Form.Group>
        <Row className="m-0">
          <Col className="ps-0">
            <Form.Group className="form-group" controlId="priceOption">
              <Form.Label>Options</Form.Label>
              <Form.Select
                name="priceOption"
                value={priceOption || ''}
                onChange={(e) => setPriceOption(e.target.value)}
              >
                {priceOpts.map((opt) => (
                  <option key={opt.id} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col className="pe-0">
            <Form.Label className="invisible" aria-hidden="true">
              Discount
            </Form.Label>
            <InputGroup
              className={`${discountError ? 'input-group-error' : ''}`}
            >
              <InputGroup.Text id="discountPrice-val">
                {priceOption?.includes('price') ? '$' : '%'}
              </InputGroup.Text>
              <Form.Control
                id="discountPrice-val"
                name="discount"
                aria-describedby="discountPrice-val discount-cap-helper"
                pattern={
                  priceOption?.includes('price') ? '^[0-9.]*$' : '^[0-9]*$'
                }
                value={discount || ''}
                onChange={(e) =>
                  setDiscount(
                    e.target.validity.valid || e.target.value === ''
                      ? e.target.value
                      : discount
                  )
                }
                onBlur={handleValid}
              />
            </InputGroup>
            <Form.Text
              id="discount-cap-helper"
              className={`d-block mt-1 caption ${
                isPercentDiscount && discountError ? 'error' : 'text-muted'
              }`}
            >
              {discountCapMessage}
            </Form.Text>
          </Col>
        </Row>
        <Form.Group className="form-group" controlId="appliedTo">
          <Form.Label>Apply discount to</Form.Label>
          <Form.Select
            name="appliedTo"
            value={appliedTo || ''}
            onChange={(e) => setAppliedTo(e.target.value)}
          >
            {applyToOpts.map((opt) => (
              <option key={opt.id} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Stack gap={3}>
          <Form.Group className="form-group mt-0" controlId="limitOption">
            <Form.Label>Code redemption limit</Form.Label>
            <Form.Select
              name="limitOption"
              value={limitOption || ''}
              onChange={(e) => setLimitOption(e.target.value)}
            >
              {limitOpts.map((opt) => (
                <option key={opt.id} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {limitOption === limitOpts[1].value && (
            <Form.Control
              type="text"
              ref={limitInputRef}
              name="limit"
              pattern={'^[0-9]*$'}
              placeholder="Enter a limit"
              value={limit || ''}
              onChange={(e) =>
                setLimit(
                  e.target.validity.valid || e.target.value === ''
                    ? e.target.value
                    : limit
                )
              }
            />
          )}
        </Stack>
      </Stack>
      <WarningCard
        text={`The discount will be applied to ${appliedTo === applyToOpts[0].value ? ' the total order amount' : ' each ticket in the order'}`}
        styles="card-with-border card--dark"
        margin="mt"
      />
    </Modal.Body>
  )
}
